import { inject, Injectable } from '@angular/core';
import { GsStructBorderRadius, GsStructBorderStyle, IUiButtonSettings } from '../../models';
import { StyleService } from './style.service';

/**
 * Injectable service for generating CSS classes from IUiButtonSettings.
 * Used by the config wizard preview and by components (e.g. Checkout) to apply global button styling.
 */
@Injectable({ providedIn: 'root' })
export class ButtonStyleHelper {
  private readonly styleService = inject(StyleService);

  /**
   * Generates all Tailwind CSS classes for a primary action button.
   * Applies background, text, border, hover, and disabled state classes.
   * @param settings Button styling settings to apply
   * @returns Space-separated string of Tailwind CSS classes
   */
  getPrimaryButtonClasses(settings: Partial<IUiButtonSettings>): string {
    // Colors, disabled and focus-visible states are shared with getPrimaryButtonColorClasses, the border with getPrimaryButtonBorderClasses
    const classes: string[] = [this.getPrimaryButtonColorClasses(settings), this.getPrimaryButtonBorderClasses(settings)];

    // Font (override textTransform when buttonTextCaps is set)
    const font = settings.font
      ? { ...settings.font, textTransform: settings.textCaps ? 'uppercase' : settings.font.textTransform }
      : undefined;
    classes.push(this.styleService.getFontClasses(font).trim());

    return classes.filter(Boolean).join(' ');
  }

  /**
   * Generates only the color-related Tailwind CSS classes for a primary action button
   * (background, text, disabled and focus-visible states) — without border, font, padding
   * or radius classes. Useful when the caller controls those aspects itself and must avoid
   * conflicting utilities (e.g. pagination buttons).
   * @param settings Button styling settings to apply
   * @returns Space-separated string of Tailwind CSS classes
   */
  getPrimaryButtonColorClasses(settings: Partial<IUiButtonSettings>): string {
    const classes: string[] = [];

    // Background colors
    classes.push(this.styleService.getColorClass(settings.backgroundColor, 'bg').trim());
    classes.push(this.styleService.getColorClass(settings.hoverBackgroundColor, 'bg', 'hover:').trim());
    classes.push(this.styleService.getColorClass(settings.disabledBackgroundColor, 'bg', 'disabled:').trim());
    classes.push(this.styleService.getColorClass(settings.disabledBackgroundColor, 'bg', 'aria-disabled:').trim());

    // Text colors
    classes.push(this.styleService.getColorClass(settings.textColor, 'text').trim());
    classes.push(this.styleService.getColorClass(settings.hoverTextColor, 'text', 'hover:').trim());
    classes.push(this.styleService.getColorClass(settings.disabledTextColor, 'text', 'disabled:').trim());
    classes.push(this.styleService.getColorClass(settings.disabledTextColor, 'text', 'aria-disabled:').trim());

    // Disabled state (supports both native disabled and aria-disabled for focusable disabled buttons)
    classes.push('disabled:cursor-not-allowed', 'aria-disabled:cursor-not-allowed');

    // Focus-visible state (use background color for outline so it is visible against the page background)
    // z-10 ensures the outline is not covered by subsequent page blocks
    classes.push('focus-visible:outline', 'focus-visible:outline-2', 'focus-visible:outline-offset-2', 'focus-visible:z-10');
    classes.push(this.styleService.getColorClass(settings.backgroundColor, 'outline', 'focus-visible:').trim());

    return classes.filter(Boolean).join(' ');
  }

  /**
   * Generates the border classes of a primary action button: width, style, color including the
   * hover and disabled states, and the radius.
   * @param settings Button styling settings to apply
   * @param maxRadius The largest radius the element accepts, e.g. for cards taking the button look over
   * @returns Space-separated string of Tailwind CSS classes
   */
  getPrimaryButtonBorderClasses(settings: Partial<IUiButtonSettings>, maxRadius?: GsStructBorderRadius): string {
    const classes: string[] = [];

    // Border — without a visible border only the radius is taken over, width and color come from the reserved border below
    const hasBorder = this.hasBorder(settings);
    if (hasBorder) {
      classes.push(this.styleService.getBorderClasses(settings.borderColor, settings.borderWidth, settings.borderStyle).trim());
    }
    classes.push(this.styleService.getBorderRadiusClass(settings.borderRadius, maxRadius));

    // No border configured: reserve 1px in the background color so the box matches the other variants without showing a border
    if (!hasBorder) {
      classes.push('border', this.styleService.getColorClass(settings.backgroundColor, 'border').trim());
    }

    // Border hover & disabled colors
    const hoverColor = hasBorder ? settings.borderHoverColor : settings.hoverBackgroundColor;
    const disabledColor = hasBorder ? settings.borderDisabledColor : settings.disabledBackgroundColor;
    classes.push(this.styleService.getColorClass(hoverColor, 'border', 'hover:').trim());
    classes.push(this.styleService.getColorClass(disabledColor, 'border', 'disabled:').trim());
    classes.push(this.styleService.getColorClass(disabledColor, 'border', 'aria-disabled:').trim());

    return classes.filter(Boolean).join(' ');
  }

  /**
   * Generates CSS classes for secondary/modal buttons.
   * Only applies borderRadius and font from the global settings.
   * Colors remain neutral (light-main bg, light-contrast text).
   * @param settings Button styling settings (only borderRadius and font are used)
   * @returns Space-separated string of Tailwind CSS classes
   */
  getSecondaryButtonClasses(settings: Partial<IUiButtonSettings>): string {
    // Colors, disabled and focus-visible states come from getSecondaryButtonColorClasses, the border from getSecondaryButtonBorderClasses
    const classes: string[] = [this.getSecondaryButtonColorClasses(), this.getSecondaryButtonBorderClasses(settings)];

    // Only font from global settings (override textTransform when buttonTextCaps is set)
    const font = settings.font
      ? { ...settings.font, textTransform: settings.textCaps ? 'uppercase' : settings.font.textTransform }
      : undefined;
    classes.push(this.styleService.getFontClasses(font).trim());

    return classes.filter(Boolean).join(' ');
  }

  /**
   * Generates only the color-related Tailwind CSS classes for a secondary button (background, text,
   * disabled and focus-visible states) — the counterpart of getPrimaryButtonColorClasses.
   * @returns Space-separated string of Tailwind CSS classes
   */
  getSecondaryButtonColorClasses(): string {
    const classes: string[] = ['bg-light-main', 'hover:bg-light-shade', 'text-light-contrast'];

    // Disabled state (supports both native disabled and aria-disabled)
    classes.push(
      'disabled:bg-light-shine',
      'disabled:hover:bg-light-shine',
      'disabled:text-light-contrast',
      'disabled:cursor-not-allowed',
      'aria-disabled:bg-light-shine',
      'aria-disabled:hover:bg-light-shine',
      'aria-disabled:cursor-not-allowed'
    );

    // Focus-visible state (light-contrast outline matches the border/text color of secondary buttons)
    classes.push(
      'focus-visible:outline',
      'focus-visible:outline-2',
      'focus-visible:outline-offset-2',
      'focus-visible:outline-light-contrast',
      'focus-visible:z-10'
    );

    return classes.join(' ');
  }

  /**
   * Generates the border classes of a secondary button: the width of the primary button in the
   * neutral light-contrast color, and the radius.
   * @param settings Button styling settings (only the border width, style and radius are used)
   * @param maxRadius The largest radius the element accepts, e.g. for cards taking the button look over
   * @returns Space-separated string of Tailwind CSS classes
   */
  getSecondaryButtonBorderClasses(settings: Partial<IUiButtonSettings>, maxRadius?: GsStructBorderRadius): string {
    const classes: string[] = ['border-light-contrast'];

    // Same border width as the primary button, so the box does not change when a control toggles between both
    const border = this.hasBorder(settings)
      ? this.styleService.getBorderClasses(undefined, settings.borderWidth, settings.borderStyle).trim()
      : '';
    classes.push(border || 'border');

    // Only borderRadius from global settings
    classes.push(this.styleService.getBorderRadiusClass(settings.borderRadius, maxRadius));

    return classes.filter(Boolean).join(' ');
  }

  /**
   * Generates CSS classes for danger buttons.
   * Keeps danger colors hardcoded, applies only borderRadius and font from settings.
   * @param settings Button styling settings (only borderRadius and font are used)
   * @returns Space-separated string of Tailwind CSS classes
   */
  getDangerButtonClasses(settings: Partial<IUiButtonSettings>): string {
    const classes: string[] = ['bg-danger-main', 'hover:bg-danger-shade', 'text-danger-contrast'];

    // Same border width as the primary button, painted in the background color since the danger button shows no border
    const border = this.hasBorder(settings)
      ? this.styleService.getBorderClasses(undefined, settings.borderWidth, settings.borderStyle).trim()
      : '';
    classes.push(border || 'border');
    classes.push('border-danger-main', 'hover:border-danger-shade', 'disabled:border-danger-shine', 'aria-disabled:border-danger-shine');

    // Only borderRadius from global settings
    const radiusClass = this.styleService.getBorderRadiusClass(settings.borderRadius);
    if (radiusClass) {
      classes.push(radiusClass);
    }

    // Only font from global settings (override textTransform when buttonTextCaps is set)
    const font = settings.font
      ? { ...settings.font, textTransform: settings.textCaps ? 'uppercase' : settings.font.textTransform }
      : undefined;
    classes.push(this.styleService.getFontClasses(font).trim());

    // Disabled state (supports both native disabled and aria-disabled)
    classes.push(
      'disabled:bg-danger-shine',
      'disabled:hover:bg-danger-shine',
      'disabled:text-danger-contrast',
      'disabled:cursor-not-allowed',
      'aria-disabled:bg-danger-shine',
      'aria-disabled:hover:bg-danger-shine',
      'aria-disabled:cursor-not-allowed'
    );

    // Focus-visible state (danger-main outline matches the button background)
    classes.push(
      'focus-visible:outline',
      'focus-visible:outline-2',
      'focus-visible:outline-offset-2',
      'focus-visible:outline-danger-main',
      'focus-visible:z-10'
    );

    return classes.filter(Boolean).join(' ');
  }

  // TODO: CS4-2461 — the CSS-resolved path needs the `gs-r-*` classes on the same element as the
  // custom properties. 34 of the 197 call sites bind the class getters without this record, so the
  // classes cannot move there without applying the configured padding to buttons that never had it.
  /**
   * Generates an ngStyle object for properties that need pixel values (padding, text size).
   * @param settings Button settings
   * @returns Record of CSS property names to values for use with [ngStyle]
   */
  getButtonStyles(settings: Partial<IUiButtonSettings>): Record<string, string> {
    const styles: Record<string, string> = {};

    // Inner padding
    const padding = settings.innerPadding;
    if (padding) {
      const resolved = this.styleService.getCurrentResponsiveValue(padding);
      if (resolved != null) {
        styles['padding-top'] = `${resolved.top}px`;
        styles['padding-right'] = `${resolved.right}px`;
        styles['padding-bottom'] = `${resolved.bottom}px`;
        styles['padding-left'] = `${resolved.left}px`;
      }
    }

    // Text size
    const textSize = settings.textSize;
    if (textSize) {
      const resolved = this.styleService.getCurrentResponsiveValue(textSize);
      if (resolved != null) {
        styles['font-size'] = `${resolved}px`;
      }
    }

    // Line height (em)
    const lineHeight = Number(settings.font?.lineHeight);
    if (Number.isFinite(lineHeight) && lineHeight > 0) {
      styles['line-height'] = `${lineHeight}em`;
    }

    return styles;
  }

  /**
   * Checks whether the settings describe a visible border (a width greater than zero and a style other than none).
   * @param settings Button styling settings to check
   * @returns True when the settings result in a border that takes up space
   */
  private hasBorder(settings: Partial<IUiButtonSettings>): boolean {
    return !!settings.borderWidth && settings.borderStyle !== GsStructBorderStyle.none && settings.borderStyle !== undefined;
  }
}
